<template>
  <div class="block-editor">
    <!-- Empty State -->
    <div v-if="!block" class="empty-state">
      <div class="empty-icon">
        <i class="bx bx-pointer"></i>
      </div>
      <h5>Propriedades do Bloco</h5>
      <p>Selecione um bloco para editar suas propriedades</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="editor-header">
        <div class="editor-header-top">
          <h5>
            <i :class="blockIcon" class="mr-1"></i>
            {{ blockLabel }}
          </h5>
          <button class="btn-close-editor" @click="$emit('close')" title="Fechar">
            <i class="bx bx-x"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="editor-content">
        <!-- TEXT BLOCK EDITOR -->
        <template v-if="block.type === 'text'">
          <div class="settings-section">
            <div class="section-title">Conteúdo</div>

            <!-- Merge Tag Buttons -->
            <div class="merge-tag-bar">
              <span class="merge-tag-label">Inserir tag:</span>
              <div class="merge-tag-buttons">
                <button
                  v-for="tag in allMergeTags"
                  :key="tag.var"
                  class="merge-tag-btn"
                  :class="'tag-' + tag.color"
                  @click="insertMergeTag(tag.var)"
                  :title="tag.var"
                >
                  {{ tag.label }}
                </button>
              </div>
            </div>

            <!-- CKEditor -->
            <div class="editor-ck-wrapper">
              <ckeditor
                :editor="editorClass"
                v-model="localContent"
                :config="editorConfig"
                @ready="onEditorReady"
                @input="onContentChange"
              />
            </div>
          </div>
        </template>

        <!-- HEADING BLOCK EDITOR -->
        <template v-else-if="block.type === 'heading'">
          <div class="settings-section">
            <div class="section-title">Texto do Título</div>
            <div class="form-group">
              <input
                type="text"
                class="form-control"
                v-model="localContent"
                placeholder="TÍTULO DA SEÇÃO"
                @input="onContentChange"
              />
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Estilo</div>
            <div class="style-options">
              <div
                class="style-option"
                :class="{ active: localSettings.style === 'banner' }"
                @click="updateSetting('style', 'banner')"
              >
                <div class="style-preview style-banner">BANNER</div>
                <span>Banner</span>
              </div>
              <div
                class="style-option"
                :class="{ active: localSettings.style === 'simple' }"
                @click="updateSetting('style', 'simple')"
              >
                <div class="style-preview style-simple">SIMPLES</div>
                <span>Simples</span>
              </div>
              <div
                class="style-option"
                :class="{ active: localSettings.style === 'underline' }"
                @click="updateSetting('style', 'underline')"
              >
                <div class="style-preview style-underline">SUBLINHADO</div>
                <span>Sublinhado</span>
              </div>
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Alinhamento</div>
            <div class="align-options">
              <button
                class="align-btn"
                :class="{ active: localSettings.alignment === 'left' }"
                @click="updateSetting('alignment', 'left')"
                title="Esquerda"
              >
                <i class="bx bx-align-left"></i>
              </button>
              <button
                class="align-btn"
                :class="{ active: localSettings.alignment === 'center' || !localSettings.alignment }"
                @click="updateSetting('alignment', 'center')"
                title="Centro"
              >
                <i class="bx bx-align-middle"></i>
              </button>
              <button
                class="align-btn"
                :class="{ active: localSettings.alignment === 'right' }"
                @click="updateSetting('alignment', 'right')"
                title="Direita"
              >
                <i class="bx bx-align-right"></i>
              </button>
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Cores</div>

            <div class="form-group color-group">
              <label>Cor de fundo</label>
              <div class="color-input-row">
                <input
                  type="color"
                  class="color-picker"
                  :value="localSettings.bgColor || '#334155'"
                  @input="updateSetting('bgColor', $event.target.value)"
                />
                <input
                  type="text"
                  class="form-control form-control-sm color-hex"
                  :value="localSettings.bgColor || '#334155'"
                  @change="updateSetting('bgColor', $event.target.value)"
                  placeholder="#334155"
                />
              </div>
              <div class="color-presets">
                <button
                  v-for="preset in bgColorPresets"
                  :key="preset"
                  class="color-preset-btn"
                  :style="{ backgroundColor: preset }"
                  :class="{ active: (localSettings.bgColor || '#334155') === preset }"
                  @click="updateSetting('bgColor', preset)"
                  :title="preset"
                ></button>
              </div>
            </div>

            <div class="form-group color-group">
              <label>Cor do texto</label>
              <div class="color-input-row">
                <input
                  type="color"
                  class="color-picker"
                  :value="localSettings.textColor || '#ffffff'"
                  @input="updateSetting('textColor', $event.target.value)"
                />
                <input
                  type="text"
                  class="form-control form-control-sm color-hex"
                  :value="localSettings.textColor || '#ffffff'"
                  @change="updateSetting('textColor', $event.target.value)"
                  placeholder="#ffffff"
                />
              </div>
              <div class="color-presets">
                <button
                  v-for="preset in textColorPresets"
                  :key="preset"
                  class="color-preset-btn"
                  :style="{ backgroundColor: preset }"
                  :class="{ active: (localSettings.textColor || '#ffffff') === preset }"
                  @click="updateSetting('textColor', preset)"
                  :title="preset"
                ></button>
              </div>
            </div>
          </div>
        </template>

        <!-- FLIGHT TABLE EDITOR -->
        <template v-else-if="block.type === 'flight-table'">
          <div class="settings-section">
            <div class="section-title">Fonte dos Dados</div>
            <div class="source-options">
              <label class="source-option" :class="{ active: (localSettings.source || 'auto') === 'auto' }">
                <input
                  type="radio"
                  name="flight-source"
                  value="auto"
                  :checked="(localSettings.source || 'auto') === 'auto'"
                  @change="updateSetting('source', 'auto')"
                />
                <div class="source-option-body">
                  <strong>Automático</strong>
                  <span>Voos cadastrados na ficha do participante</span>
                </div>
              </label>
              <label class="source-option" :class="{ active: localSettings.source === 'manual' }">
                <input
                  type="radio"
                  name="flight-source"
                  value="manual"
                  :checked="localSettings.source === 'manual'"
                  @change="updateSetting('source', 'manual')"
                />
                <div class="source-option-body">
                  <strong>Manual</strong>
                  <span>Mesmos voos/charter para todos</span>
                </div>
              </label>
              <label class="source-option" :class="{ active: localSettings.source === 'misto' }">
                <input
                  type="radio"
                  name="flight-source"
                  value="misto"
                  :checked="localSettings.source === 'misto'"
                  @change="updateSetting('source', 'misto')"
                />
                <div class="source-option-body">
                  <strong>Misto</strong>
                  <span>Usa ficha; se participante sem voo, usa manual</span>
                </div>
              </label>
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Configurações da Tabela</div>

            <div class="form-group toggle-group">
              <label>Exibir título da tabela</label>
              <b-form-checkbox
                v-model="localSettings.showTitle"
                switch
                @change="updateSetting('showTitle', $event)"
              />
            </div>

            <div v-if="localSettings.showTitle !== false" class="form-group">
              <label>Título</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.title"
                placeholder="VOOS CONFIRMADOS"
                @input="updateSetting('title', $event.target.value)"
              />
            </div>

            <div class="form-group toggle-group">
              <label>Exibir Localizador</label>
              <b-form-checkbox
                v-model="localSettings.showLocalizador"
                switch
                @change="updateSetting('showLocalizador', $event)"
              />
            </div>

            <div class="form-group toggle-group">
              <label>Exibir E-Ticket</label>
              <b-form-checkbox
                v-model="localSettings.showEticket"
                switch
                @change="updateSetting('showEticket', $event)"
              />
            </div>
          </div>

          <div v-if="(localSettings.source || 'auto') !== 'auto'" class="settings-section">
            <div class="section-title">Dados Manuais</div>
            <div class="form-group">
              <label>Descrição dos voos (texto livre)</label>
              <textarea
                class="form-control"
                rows="4"
                v-model="localSettings.manual_text"
                placeholder="Ex: Voo LA3400 - 15/05/2026 - GRU → MIA - Saída 20h00 / Chegada 07h30"
                @input="updateSetting('manual_text', $event.target.value)"
              ></textarea>
            </div>
            <div v-if="localSettings.showLocalizador !== false" class="form-group">
              <label>Localizador (manual)</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.manual_locator"
                placeholder="Ex: X7Y2AB"
                @input="updateSetting('manual_locator', $event.target.value)"
              />
            </div>
            <div v-if="localSettings.showEticket" class="form-group">
              <label>E-ticket (manual)</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.manual_eticket"
                placeholder="Ex: 957-2401234567"
                @input="updateSetting('manual_eticket', $event.target.value)"
              />
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Informação</div>
            <div class="info-box">
              <i class="bx bx-info-circle"></i>
              <p v-if="(localSettings.source || 'auto') === 'auto'">
                A tabela será preenchida automaticamente com os dados de transporte do participante ao gerar o documento.
              </p>
              <p v-else-if="localSettings.source === 'manual'">
                Os dados preenchidos aqui serão aplicados <strong>a todos</strong> os participantes que receberem esta Carta Pax.
              </p>
              <p v-else>
                Tenta usar os voos cadastrados na ficha do participante; se não houver, cai no texto manual.
              </p>
            </div>
          </div>
        </template>

        <!-- ACCOMMODATION EDITOR -->
        <template v-else-if="block.type === 'accommodation'">
          <div class="settings-section">
            <div class="section-title">Fonte dos Dados</div>
            <div class="source-options">
              <label class="source-option" :class="{ active: (localSettings.source || 'auto') === 'auto' }">
                <input
                  type="radio"
                  name="acc-source"
                  value="auto"
                  :checked="(localSettings.source || 'auto') === 'auto'"
                  @change="updateSetting('source', 'auto')"
                />
                <div class="source-option-body">
                  <strong>Automático</strong>
                  <span>Puxa do módulo de Hospedagem (por participante)</span>
                </div>
              </label>
              <label class="source-option" :class="{ active: localSettings.source === 'manual' }">
                <input
                  type="radio"
                  name="acc-source"
                  value="manual"
                  :checked="localSettings.source === 'manual'"
                  @change="updateSetting('source', 'manual')"
                />
                <div class="source-option-body">
                  <strong>Manual</strong>
                  <span>Mesmo hotel para todos (preenchido abaixo)</span>
                </div>
              </label>
              <label class="source-option" :class="{ active: localSettings.source === 'misto' }">
                <input
                  type="radio"
                  name="acc-source"
                  value="misto"
                  :checked="localSettings.source === 'misto'"
                  @change="updateSetting('source', 'misto')"
                />
                <div class="source-option-body">
                  <strong>Misto</strong>
                  <span>Usa o módulo; se participante sem alocação, usa dados manuais</span>
                </div>
              </label>
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Título da Seção</div>
            <div class="form-group toggle-group">
              <label>Exibir título</label>
              <b-form-checkbox
                v-model="localSettings.showTitle"
                switch
                @change="updateSetting('showTitle', $event)"
              />
            </div>
            <div v-if="localSettings.showTitle !== false" class="form-group">
              <input
                type="text"
                class="form-control"
                v-model="localSettings.title"
                placeholder="HOSPEDAGEM"
                @input="updateSetting('title', $event.target.value)"
              />
            </div>
          </div>

          <div v-if="(localSettings.source || 'auto') !== 'auto'" class="settings-section">
            <div class="section-title">Dados Manuais</div>
            <div class="form-group">
              <label>Nome do hotel</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.hotel_name"
                placeholder="Ex: Hotel Fasano Rio"
                @input="updateSetting('hotel_name', $event.target.value)"
              />
            </div>
            <div class="form-group">
              <label>Endereço</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.hotel_address"
                placeholder="Av. Vieira Souto, 80 - Ipanema/RJ"
                @input="updateSetting('hotel_address', $event.target.value)"
              />
            </div>
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label>Check-in</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="localSettings.check_in"
                    @input="updateSetting('check_in', $event.target.value)"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <label>Check-out</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="localSettings.check_out"
                    @input="updateSetting('check_out', $event.target.value)"
                  />
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>Tipo de quarto</label>
              <input
                type="text"
                class="form-control"
                v-model="localSettings.room_type"
                placeholder="Ex: Duplo Cama Casal"
                @input="updateSetting('room_type', $event.target.value)"
              />
            </div>
            <div class="form-group">
              <label>Observações</label>
              <textarea
                class="form-control"
                rows="2"
                v-model="localSettings.notes"
                placeholder="Ex: Café da manhã incluso"
                @input="updateSetting('notes', $event.target.value)"
              ></textarea>
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Exibição</div>
            <div v-if="(localSettings.source || 'auto') !== 'manual'" class="form-group toggle-group">
              <label>Exibir acompanhantes de quarto</label>
              <b-form-checkbox
                v-model="localSettings.showCompanions"
                switch
                @change="updateSetting('showCompanions', $event)"
              />
            </div>
            <div v-if="(localSettings.source || 'auto') !== 'manual'" class="form-group toggle-group">
              <label>Exibir categoria do quarto</label>
              <b-form-checkbox
                v-model="localSettings.showCategory"
                switch
                @change="updateSetting('showCategory', $event)"
              />
            </div>
            <div class="form-group toggle-group">
              <label>Exibir observações</label>
              <b-form-checkbox
                v-model="localSettings.showNotes"
                switch
                @change="updateSetting('showNotes', $event)"
              />
            </div>
          </div>

          <div class="settings-section">
            <div class="section-title">Informação</div>
            <div class="info-box">
              <i class="bx bx-info-circle"></i>
              <p v-if="(localSettings.source || 'auto') === 'auto'">
                Dados serão puxados do módulo de <strong>Gestão de Hospedagem</strong>.
                Se o participante não estiver alocado, a seção não será renderizada.
              </p>
              <p v-else-if="localSettings.source === 'manual'">
                Os dados preenchidos aqui serão aplicados <strong>a todos</strong> os participantes que receberem esta Carta Pax.
              </p>
              <p v-else>
                Tenta usar o módulo de Hospedagem; se o participante não estiver alocado, cai nos dados manuais preenchidos aqui.
              </p>
            </div>
          </div>
        </template>

        <!-- DIVIDER EDITOR -->
        <template v-else-if="block.type === 'divider'">
          <div class="settings-section">
            <div class="section-title">Estilo da Linha</div>
            <div class="style-options">
              <div
                class="style-option"
                :class="{ active: (localSettings.style || 'solid') === 'solid' }"
                @click="updateSetting('style', 'solid')"
              >
                <div class="divider-style-preview"><hr style="border-top:1px solid #adb5bd;"></div>
                <span>Sólida</span>
              </div>
              <div
                class="style-option"
                :class="{ active: localSettings.style === 'dashed' }"
                @click="updateSetting('style', 'dashed')"
              >
                <div class="divider-style-preview"><hr style="border-top:1px dashed #adb5bd;"></div>
                <span>Tracejada</span>
              </div>
              <div
                class="style-option"
                :class="{ active: localSettings.style === 'double' }"
                @click="updateSetting('style', 'double')"
              >
                <div class="divider-style-preview"><hr style="border-top:3px double #adb5bd;"></div>
                <span>Dupla</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

var contentUpdateTimeout = null

export default {
  name: 'BlockEditor',
  components: {
    ckeditor: CKEditor.component
  },
  props: {
    block: { type: Object, default: null }
  },
  data: function () {
    return {
      editorClass: ClassicEditor,
      editorInstance: null,
      localContent: '',
      localSettings: {},
      editorConfig: {
        language: 'pt-br',
        toolbar: {
          items: [
            'bold', 'italic', 'underline', '|',
            'bulletedList', 'numberedList', '|',
            'alignment', '|',
            'undo', 'redo'
          ]
        }
      },
      bgColorPresets: [
        '#334155', '#1e293b', '#0f172a', '#1e3a5f', '#1a365d',
        '#14532d', '#713f12', '#7f1d1d', '#581c87', '#4a5568'
      ],
      textColorPresets: [
        '#ffffff', '#f8fafc', '#e2e8f0', '#fef3c7', '#1e293b'
      ],
      allMergeTags: [
        { var: '{$nome}', label: 'Nome', color: 'blue' },
        { var: '{$email}', label: 'Email', color: 'blue' },
        { var: '{$cpf}', label: 'CPF', color: 'blue' },
        { var: '{$phone}', label: 'Fone', color: 'blue' },
        { var: '{$grupo}', label: 'Grupo', color: 'blue' },
        { var: '{$evento}', label: 'Evento', color: 'green' },
        { var: '{$data_atual}', label: 'Data', color: 'green' },
        { var: '{$localizador}', label: 'Localiz.', color: 'purple' },
        { var: '{$eticket}', label: 'E-Ticket', color: 'purple' },
        { var: '{$hotel}', label: 'Hotel', color: 'teal' },
        { var: '{$hotel_checkin}', label: 'Check-in', color: 'teal' },
        { var: '{$hotel_checkout}', label: 'Check-out', color: 'teal' },
        { var: '{$quarto_tipo}', label: 'Quarto', color: 'teal' }
      ]
    }
  },
  watch: {
    block: {
      handler: function (val) {
        if (val) {
          this.localContent = val.content || ''
          this.localSettings = Object.assign({}, val.settings || {})
        } else {
          this.localContent = ''
          this.localSettings = {}
          this.editorInstance = null
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    blockIcon: function () {
      var icons = {
        text: 'bx bx-text',
        heading: 'bx bx-heading',
        'flight-table': 'bx bx-table',
        accommodation: 'bx bx-bed',
        divider: 'bx bx-minus'
      }
      return (this.block && icons[this.block.type]) || 'bx bx-cube'
    },
    blockLabel: function () {
      var labels = {
        text: 'Texto Rico',
        heading: 'Título de Seção',
        'flight-table': 'Tabela de Voos',
        accommodation: 'Hospedagem',
        divider: 'Separador'
      }
      return (this.block && labels[this.block.type]) || 'Bloco'
    }
  },
  methods: {
    onEditorReady: function (editor) {
      this.editorInstance = editor
    },
    onContentChange: function () {
      var self = this
      if (contentUpdateTimeout) clearTimeout(contentUpdateTimeout)
      contentUpdateTimeout = setTimeout(function () {
        self.$emit('update', {
          id: self.block._id,
          data: { content: self.localContent }
        })
      }, 300)
    },
    updateSetting: function (key, value) {
      this.localSettings[key] = value
      this.$emit('update', {
        id: this.block._id,
        data: { settings: Object.assign({}, this.localSettings) }
      })
    },
    insertMergeTag: function (variable) {
      if (this.editorInstance) {
        var viewFragment = this.editorInstance.data.processor.toView(
          '<strong>' + variable + '</strong>&nbsp;'
        )
        var modelFragment = this.editorInstance.data.toModel(viewFragment)
        this.editorInstance.model.insertContent(modelFragment)
        this.editorInstance.editing.view.focus()
      } else {
        // Fallback: append to content
        this.localContent += variable
        this.onContentChange()
      }
    }
  }
}
</script>

<style scoped>
.block-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 28px;
  color: #556ee6;
}

.empty-state h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.empty-state p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Header */
.editor-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.editor-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.editor-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
}

.editor-header h5 i {
  color: #556ee6;
  font-size: 18px;
}

.btn-close-editor {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-close-editor:hover {
  background: #f3f4f6;
  color: #344054;
}

/* Content */
.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.settings-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
}

.settings-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

.toggle-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-group label {
  margin-bottom: 0;
}

/* Merge tag bar */
.merge-tag-bar {
  margin-bottom: 10px;
}

.merge-tag-label {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  display: block;
  margin-bottom: 6px;
}

.merge-tag-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.merge-tag-btn {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.12s;
}

.merge-tag-btn.tag-blue {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #bbdefb;
}
.merge-tag-btn.tag-blue:hover {
  background: #bbdefb;
}

.merge-tag-btn.tag-green {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #c8e6c9;
}
.merge-tag-btn.tag-green:hover {
  background: #c8e6c9;
}

.merge-tag-btn.tag-purple {
  background: #f3e5f5;
  color: #7b1fa2;
  border-color: #e1bee7;
}
.merge-tag-btn.tag-purple:hover {
  background: #e1bee7;
}

.merge-tag-btn.tag-teal {
  background: #e0f2f1;
  color: #00695c;
  border-color: #b2dfdb;
}
.merge-tag-btn.tag-teal:hover {
  background: #b2dfdb;
}

/* Source option (auto/manual/misto radios) */
.source-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.source-option {
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 0;
  align-items: flex-start;
}

.source-option:hover {
  border-color: #d0d5dd;
  background: #fafbfc;
}

.source-option.active {
  border-color: #556ee6;
  background: #f0f4ff;
}

.source-option input[type="radio"] {
  margin-top: 3px;
  flex-shrink: 0;
}

.source-option-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.source-option-body strong {
  font-size: 13px;
  color: #344054;
}

.source-option-body span {
  font-size: 11px;
  color: #6c757d;
  line-height: 1.4;
}

/* CKEditor wrapper */
.editor-ck-wrapper {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.editor-ck-wrapper >>> .ck-editor__editable {
  min-height: 200px;
  max-height: 400px;
  font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.6;
  padding: 12px;
}

.editor-ck-wrapper >>> .ck.ck-toolbar {
  border-top: none;
  border-left: none;
  border-right: none;
  background: #f8f9fa;
}

.editor-ck-wrapper >>> .ck.ck-editor__main > .ck-editor__editable {
  border-left: none;
  border-right: none;
  border-bottom: none;
}

/* Heading style options */
.style-options {
  display: flex;
  gap: 8px;
}

.style-option {
  flex: 1;
  padding: 8px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;
}

.style-option:hover {
  border-color: #d0d5dd;
}

.style-option.active {
  border-color: #556ee6;
  background: #f0f4ff;
}

.style-option span {
  display: block;
  font-size: 10px;
  font-weight: 500;
  color: #495057;
  margin-top: 6px;
}

.style-preview {
  font-size: 9px;
  font-weight: bold;
  padding: 4px 6px;
  border-radius: 2px;
}

.style-banner {
  background: #2c3e50;
  color: #fff;
}

.style-simple {
  font-style: italic;
  color: #344054;
}

.style-underline {
  border-bottom: 2px solid #2c3e50;
  color: #344054;
  padding-bottom: 2px;
}

.divider-style-preview {
  padding: 8px 0;
}

.divider-style-preview hr {
  margin: 0;
}

/* Alignment */
.align-options {
  display: flex;
  gap: 4px;
}

.align-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  font-size: 18px;
  transition: all 0.15s;
}

.align-btn:hover {
  border-color: #d0d5dd;
  color: #495057;
}

.align-btn.active {
  border-color: #556ee6;
  background: #f0f4ff;
  color: #556ee6;
}

/* Info box */
.info-box {
  display: flex;
  gap: 8px;
  background: #f0f4ff;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  color: #495057;
  line-height: 1.5;
}

.info-box i {
  color: #556ee6;
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-box p {
  margin: 0;
}

/* Color picker */
.color-group {
  margin-bottom: 16px;
}

.color-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.color-picker {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  background: #fff;
  flex-shrink: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 3px;
}

.color-hex {
  font-family: monospace;
  font-size: 12px !important;
  letter-spacing: 0.5px;
}

.color-presets {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.color-preset-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.12s;
  padding: 0;
}

.color-preset-btn:hover {
  transform: scale(1.15);
}

.color-preset-btn.active {
  border-color: #556ee6;
  box-shadow: 0 0 0 2px rgba(85, 110, 230, 0.3);
}
</style>
